import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OurAuthService } from 'app/core/auth/auth.service';
import { UserEventsSocketService } from 'app/core/user-events/user-events-socket.service';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { environment } from 'environments/environment';
import {
    BehaviorSubject,
    defer,
    distinctUntilChanged,
    EMPTY,
    filter,
    firstValueFrom,
    map,
    Observable,
    Subscription,
    take,
} from 'rxjs';

import { ExternalDatatype } from '../../../data-source-table-route.util';

interface ExtractionTriggerBody {
    tableName: string;
    dataSourceId: string | number;
    threshold: number;
    sourceType?: ExternalDatatype;
    /** When set, regenerate only that department's Internal/External metadata scope. */
    departmentId?: number | null;
}

interface PendingExtractionJob {
    tableName: string;
    dataSourceId: string;
    tableId?: string;
    hasBeenSeenInProgress: boolean;
}

interface ExtractionTriggerResponse {
    success?: boolean;
    data?: {
        accepted?: boolean;
        tableId?: string | number;
        tableType?: string;
        tableName?: string;
    };
    statusCode?: number;
    message?: string;
}

export interface PendingTableExtractionJob {
    tableName: string;
    dataSourceId: string;
    tableId?: string;
}

@Injectable({ providedIn: 'root' })
export class TableStructureBackgroundService {
    /** Shared multiplex socket client (core); connect/reconnect/frame parsing live there. */
    private readonly userEvents = inject(UserEventsSocketService);
    private readonly auth = inject(OurAuthService);
    /** Active `table-description` subscription; held while jobs are pending or a page syncs. */
    private eventsSubscription: Subscription | null = null;
    private pendingJobs = new Map<string, PendingExtractionJob>();
    /** The data source page currently in view (replaced on navigation, never accumulated). */
    private activeDataSourceId: string | null = null;
    private completionWaiters = new Map<
        string,
        { resolve: () => void; reject: (error?: unknown) => void }
    >();

    /** Increments whenever a table-description job is registered or cleared (for OnPush parents). */
    private readonly pendingJobsRevision = new BehaviorSubject(0);
    readonly pendingJobsRevision$ = this.pendingJobsRevision.asObservable();

    constructor(
        private readonly http: HttpClient,
        private readonly alert: CustomAlertService
    ) {
        // Root service outlives the tables page — drop the live subscription + pending
        // jobs on logout so completion toasts cannot fire after sign-out.
        this.auth.isLoggedIn$
            .pipe(distinctUntilChanged())
            .subscribe((loggedIn: boolean): void => {
                if (!loggedIn) {
                    this.clearSession();
                }
            });
    }

    /**
     * Drop pending extraction state and release the `table-description` subscription.
     * Called on logout so a still-open multiplex socket cannot toast after sign-out.
     */
    clearSession(): void {
        for (const [key, waiter] of [...this.completionWaiters.entries()]) {
            this.completionWaiters.delete(key);
            waiter.resolve();
        }
        this.pendingJobs.clear();
        this.activeDataSourceId = null;
        if (this.eventsSubscription) {
            this.eventsSubscription.unsubscribe();
            this.eventsSubscription = null;
        }
        this.bumpPendingJobsRevision();
    }

    async triggerExtraction(body: ExtractionTriggerBody): Promise<ExtractionTriggerResponse> {
        const response = await firstValueFrom(
            this.http.post(
                `${environment.apiLink}data_source/generate-table-desc`,
                body
            )
        ) as ExtractionTriggerResponse;
        const resolvedTableName = String(
            response?.data?.tableName ?? body.tableName
        ).trim();
        const key = this.buildJobKey(body.dataSourceId, resolvedTableName);
        const tableId =
            response?.data?.tableId != null
                ? String(response.data.tableId)
                : undefined;
        this.pendingJobs.set(key, {
            tableName: resolvedTableName,
            dataSourceId: String(body.dataSourceId),
            tableId,
            hasBeenSeenInProgress: false,
        });
        this.bumpPendingJobsRevision();
        this.ensureSubscribed();
        this.userEvents.requestSnapshot();
        return response;
    }

    isExtractionPending(body: {
        tableName?: string;
        dataSourceId: string | number;
        tableId?: string | number;
    }): boolean {
        const dsStr = String(body.dataSourceId).trim();
        const tableName = String(body.tableName ?? '').trim();
        const tableId =
            body.tableId != null ? String(body.tableId).trim() : '';

        if (tableName) {
            const key = this.buildJobKey(dsStr, tableName);
            if (this.pendingJobs.has(key)) {
                return true;
            }
        }

        if (tableId) {
            for (const job of this.pendingJobs.values()) {
                if (job.dataSourceId === dsStr && job.tableId === tableId) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Emits once when a table that was NOT extracting starts extracting.
     *
     * The server re-extracts a table on its own when it notices the source
     * structure changed - and it publishes the same `table-description` entry a
     * manual run does. A caller showing that table's data needs to know, because
     * what it is showing is being rebuilt underneath it.
     *
     * Completes immediately (without emitting) when the table is already
     * extracting: callers care about the transition, not the standing state, and
     * an already-running extraction is the caller's own to handle.
     */
    whenExtractionStarts(body: {
        tableName: string;
        dataSourceId: string | number;
    }): Observable<void> {
        return defer(() => {
            if (this.isExtractionPending(body)) {
                return EMPTY;
            }
            // Ask for the snapshot the entry will arrive in; the subscription
            // below is already live by the time any frame is processed.
            this.syncPendingExtractionState(body.dataSourceId);
            return this.pendingJobsRevision$.pipe(
                filter(() => this.isExtractionPending(body)),
                take(1),
                map(() => undefined as void)
            );
        });
    }

    getPendingJobsForDataSource(
        dataSourceId: string | number
    ): PendingTableExtractionJob[] {
        const dsStr = String(dataSourceId).trim();
        return Array.from(this.pendingJobs.values())
            .filter(job => job.dataSourceId === dsStr)
            .map(({ tableName, dataSourceId, tableId }) => ({
                tableName,
                dataSourceId,
                tableId,
            }));
    }

    /**
     * Keep a `table-description` subscription live so snapshots can repopulate
     * in-memory pending state after a full page reload (pending map is not persisted).
     */
    syncPendingExtractionState(dataSourceId?: string | number): void {
        if (dataSourceId != null && String(dataSourceId).trim() !== '') {
            this.activeDataSourceId = String(dataSourceId).trim();
            this.purgeMisbindingJobsForDataSource(this.activeDataSourceId);
        }
        this.ensureSubscribed();
        this.userEvents.requestSnapshot();
    }

    /**
     * When the tables REST payload includes generating flags, register pending jobs before
     * the first socket message arrives so card disable / loaders work immediately after refresh.
     */
    hydratePendingFromTablesSnapshot(
        tables: unknown[] | null | undefined,
        dataSourceId: string | number
    ): void {
        if (!Array.isArray(tables) || tables.length === 0) {
            return;
        }
        const defaultDs = String(dataSourceId).trim();
        let changed = false;
        for (const raw of tables) {
            if (!raw || typeof raw !== 'object') {
                continue;
            }
            const row = raw as Record<string, unknown>;
            const generating = Boolean(row['generatingDesc'] ?? row['generating']);
            if (!generating) {
                continue;
            }
            const tableName = String(
                row['value'] ??
                    row['tableName'] ??
                    row['name'] ??
                    row['userTableName'] ??
                    row['label'] ??
                    ''
            ).trim();
            if (!tableName) {
                continue;
            }
            const rowDsRaw =
                row['dataSourceId'] ?? row['dsId'] ?? row['data_source_id'];
            const dsStr =
                rowDsRaw != null && String(rowDsRaw).trim() !== ''
                    ? String(rowDsRaw).trim()
                    : defaultDs;
            const key = this.buildJobKey(dsStr, tableName);
            const tableIdRaw =
                row['tableId'] ?? row['id'] ?? row['filterId'];
            const tableId =
                tableIdRaw != null ? String(tableIdRaw).trim() : '';
            if (this.ensurePendingJobForGeneratingRow(key, tableName, dsStr, tableId)) {
                changed = true;
            }
        }
        if (changed) {
            this.bumpPendingJobsRevision();
            // Hydrated jobs need the live stream to ever resolve.
            this.ensureSubscribed();
        }
    }

    waitForCompletion(body: {
        tableName: string;
        dataSourceId: string | number;
        timeoutMs?: number;
    }): Promise<void> {
        const key = this.buildJobKey(body.dataSourceId, body.tableName);
        if (!this.pendingJobs.has(key)) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
            if ((body.timeoutMs ?? 0) > 0) {
                timeoutHandle = setTimeout(() => {
                    this.completionWaiters.delete(key);
                    reject(new Error('Extraction is still running in background.'));
                }, body.timeoutMs);
            }

            this.completionWaiters.set(key, {
                resolve: () => {
                    if (timeoutHandle) clearTimeout(timeoutHandle);
                    resolve();
                },
                reject: (error?: unknown) => {
                    if (timeoutHandle) clearTimeout(timeoutHandle);
                    reject(error);
                },
            });
        });
    }

    /**
     * Subscribe to `table-description` frames on the shared userEvents client.
     * The core service owns connect, reconnect, and frame parsing; subscribing
     * keeps the shared socket alive.
     */
    private ensureSubscribed(): void {
        if (this.eventsSubscription && !this.eventsSubscription.closed) {
            return;
        }
        this.eventsSubscription = this.userEvents
            .on('table-description')
            .subscribe(data => this.handleTableDescriptionData(data));
    }

    /** Release the shared socket once nothing is pending (refcount may close it). */
    private releaseSubscriptionIfIdle(): void {
        if (this.pendingJobs.size === 0 && this.eventsSubscription) {
            this.eventsSubscription.unsubscribe();
            this.eventsSubscription = null;
        }
    }

    private handleTableDescriptionData(data: unknown): void {
        // Guard against in-flight frames that arrive during / after logout teardown.
        if (!this.auth.accessToken) {
            return;
        }
        if (!Array.isArray(data)) {
            return;
        }

        const rows = data.filter(
            v => v && typeof v === 'object'
        ) as Record<string, unknown>[];

        const activeKeys = new Set<string>();
        /** Scoped as `dataSourceId::tableId` so the same table id on different sources never collides. */
        const activeTableIds = new Set<string>();

        for (const row of rows) {
            const generating = Boolean(row.generatingDesc ?? row.generating);
            if (!generating) {
                continue;
            }
            const rowTableId =
                row.tableId != null ? String(row.tableId).trim() : '';
            const rowTableName = String(row.tableName ?? row.table ?? '').trim();
            if (!rowTableName) {
                continue;
            }
            const rowDataSourceId =
                row.dataSourceId ?? row.dsId ?? row.data_source_id;
            const dsId = this.resolveDataSourceIdForSocketRow(
                rowDataSourceId,
                rowTableId,
                rowTableName
            );
            if (!dsId) {
                continue;
            }
            if (rowTableId) {
                activeTableIds.add(this.buildTableIdKey(dsId, rowTableId));
            }
            const key = this.buildJobKey(dsId, rowTableName);
            activeKeys.add(key);
            this.ensurePendingJobForGeneratingRow(
                key,
                rowTableName,
                dsId,
                rowTableId
            );
        }

        /** Empty `data` is an authoritative snapshot: no table is generating. */
        if (rows.length === 0) {
            for (const [key, job] of [...this.pendingJobs.entries()]) {
                if (job.hasBeenSeenInProgress) {
                    this.alert.successMessage(
                        `Table structure extraction completed for ${job.tableName}.`
                    );
                }
                this.markJobCompleted(key);
            }
            this.releaseSubscriptionIfIdle();
            return;
        }

        for (const [key, job] of this.pendingJobs.entries()) {
            const matchedById = job.tableId
                ? activeTableIds.has(
                      this.buildTableIdKey(job.dataSourceId, job.tableId)
                  )
                : false;
            const matchedByKey = activeKeys.has(key);
            const isActive = matchedById || matchedByKey;
            if (isActive) {
                job.hasBeenSeenInProgress = true;
                this.pendingJobs.set(key, job);
                continue;
            }
            if (job.hasBeenSeenInProgress) {
                this.alert.successMessage(
                    `Table structure extraction completed for ${job.tableName}.`
                );
                this.markJobCompleted(key);
            }
        }
        this.bumpPendingJobsRevision();
        this.releaseSubscriptionIfIdle();
    }

    /** @returns true if a new pending entry was created */
    private ensurePendingJobForGeneratingRow(
        key: string,
        tableName: string,
        dataSourceIdStr: string,
        tableId: string
    ): boolean {
        const tid = tableId.trim() ? tableId : undefined;
        const prev = this.pendingJobs.get(key);
        if (!prev) {
            this.pendingJobs.set(key, {
                tableName,
                dataSourceId: dataSourceIdStr,
                tableId: tid,
                hasBeenSeenInProgress: true,
            });
            return true;
        }
        if (tid && !prev.tableId) {
            prev.tableId = tid;
        }
        prev.hasBeenSeenInProgress = true;
        this.pendingJobs.set(key, prev);
        return false;
    }

    /**
     * Socket snapshots often omit `dataSourceId`. Resolve to exactly one data source -
     * never fan out to every page the user has visited in this session.
     */
    private resolveDataSourceIdForSocketRow(
        rowDataSourceId: unknown,
        rowTableId: string,
        rowTableName: string
    ): string | null {
        if (rowDataSourceId != null && String(rowDataSourceId).trim() !== '') {
            return String(rowDataSourceId).trim();
        }

        if (rowTableId) {
            const matches = Array.from(this.pendingJobs.values()).filter(
                job => job.tableId === rowTableId
            );
            if (matches.length === 1) {
                return matches[0].dataSourceId;
            }
        }

        const normalizedName = rowTableName.trim().toLowerCase();
        const matchingByName = Array.from(this.pendingJobs.values()).filter(
            job => job.tableName.trim().toLowerCase() === normalizedName
        );
        if (matchingByName.length === 1) {
            return matchingByName[0].dataSourceId;
        }

        if (this.pendingJobs.size === 1) {
            return Array.from(this.pendingJobs.values())[0].dataSourceId;
        }

        return this.activeDataSourceId;
    }

    /** Remove jobs bound to `dsId` that duplicate an in-flight job on another data source. */
    private purgeMisbindingJobsForDataSource(dsId: string): void {
        const foreignTableIds = new Set<string>();
        for (const job of this.pendingJobs.values()) {
            if (job.dataSourceId !== dsId && job.tableId) {
                foreignTableIds.add(job.tableId);
            }
        }
        if (foreignTableIds.size === 0) {
            return;
        }

        let changed = false;
        for (const [key, job] of this.pendingJobs.entries()) {
            if (
                job.dataSourceId === dsId &&
                job.tableId &&
                foreignTableIds.has(job.tableId)
            ) {
                this.pendingJobs.delete(key);
                changed = true;
            }
        }
        if (changed) {
            this.bumpPendingJobsRevision();
        }
    }

    private buildJobKey(dataSourceId: string | number, tableName: string): string {
        return `${String(dataSourceId).trim()}::${String(tableName).trim().toLowerCase()}`;
    }

    private buildTableIdKey(
        dataSourceId: string | number,
        tableId: string | number
    ): string {
        return `${String(dataSourceId).trim()}::${String(tableId).trim()}`;
    }

    private markJobCompleted(key: string): void {
        this.pendingJobs.delete(key);
        this.bumpPendingJobsRevision();
        const waiter = this.completionWaiters.get(key);
        if (waiter) {
            this.completionWaiters.delete(key);
            waiter.resolve();
        }
        this.releaseSubscriptionIfIdle();
    }

    private markJobFailed(key: string, error?: unknown): void {
        this.pendingJobs.delete(key);
        this.bumpPendingJobsRevision();
        const waiter = this.completionWaiters.get(key);
        if (waiter) {
            this.completionWaiters.delete(key);
            waiter.reject(error);
        }
        this.releaseSubscriptionIfIdle();
    }

    private bumpPendingJobsRevision(): void {
        this.pendingJobsRevision.next(this.pendingJobsRevision.value + 1);
    }

}
